import { serializeAttachmentUpload } from '@services/attachmentChanges/attachmentUploadOrigins';
import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import { BACKGROUND_START_REQUEST } from '@services/BackgroundJobsService';
import { IVoteWithSignResult, IVotingWithSignModel } from '@models/document/actionsExecution/IVotingWithSignModel';
import { IRunTaskModel } from '@models/document/actionsExecution/IRunTaskModel';
import { IDocumentTable, IForms } from '@models/Forms/IForms';
import { IFormData } from '@models/IFormData';
import { IDocumentData } from '@models/document/IDocumentData';
import { IOpenDocumentModel } from '@models/document/actionsExecution/IOpenDocumentModel';
import { ISendToEtpModel } from '@models/document/actionsExecution/ISendToEtpModel';
import { ICopyAttachmentsModel, ICopyAttachmentsResult } from '@models/document/actionsExecution/ICopyAttachmentsModel';
import { ICreateDocByTemplateModel } from '@models/document/actionsExecution/ICreateDocByTemplateModel';
import { ICreateDocByTemplateDialogModel } from '@models/document/actionsExecution/ICreateDocByTemplateDialogModel';
import { IMailReminderModel } from '@models/document/actionsExecution/IMailReminderModel';
import { ICreateDocumentLinkModel } from '@models/document/actionsExecution/ICreateDocumentLinkModel';
import { ISendToEisModel, ISendToEisPackageSize } from '@models/document/actionsExecution/ISendToEisModel';
import { IDocumentResult } from '@models/document/IDocumentResult';
import { IOpenUrlModel } from '@models/document/actionsExecution/IOpenUrlModel';
import { ICreateReportModel } from '@models/document/actionsExecution/ICreateReportModel';
import { IVoteResult, IVotingModel } from '@models/document/actionsExecution/IVotingModel';
import { ICreateDocumentLinkData } from '@models/document/actionsExecution/ICreateDocumentLinkData';
import { IFillDocFromTemplateModel } from '@models/fillDocFromTemplate/IFillDocFromTemplateModel';
import { IActionResult } from '@models/actions/IActionResult';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { IBatchEditingTableData } from '@models/actions/IBatchEditingTableData';
import { ICopyAndEditAttachesResponse } from '@models/attaches/ICopyAndEditAttaches';
import { IEditDocumentTemplateModel } from '@organisms/actions/docExecutors/EditDocumentTemplate/IEditDocumentTemplateFormData';
import { createUploadProgressHandler, UploadProgressHandler } from '@utils/uploadProgress';
import type { IActionReason } from '@organisms/actions/ActionReason/actionReason';
import { withGroupRun } from './groupOperationRun';

export class DocumentExecutionService {
    documentId: string;

    constructor(docId: string) {
        this.documentId = docId;
    }

    //Отправить голос с подписью по вопросу
    async sendSignedVote(actKey: string, voteData: Record<string, IVoteWithSignResult>): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/sendSignedVote/${this.documentId}/${actKey}`, voteData);
    }

    //Отправить голос по вопросу
    async sendVote(actKey: string, voteData: Record<string, IVoteResult>): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/sendVote/${this.documentId}/${actKey}`, voteData);
    }

    //Запустить документ по маршруту
    /** `reason` — причина запуска, если у кнопки `requireReason` (технологический маршрут). */
    async runRoute(actionKey: string, reason?: IActionReason): Promise<AxiosResponse> {
        const url = `/documentExecution/runRoute/${this.documentId}/${actionKey}`;
        return withGroupRun(actionKey, (params) => {
            if (reason) return params ? $api.post(url, reason, { params }) : $api.post(url, reason);
            return params ? $api.post(url, undefined, { params }) : $api.post(url);
        });
    }

    //Получить модель для голосования/подписания по вопросу (инфа для модальки + json для подписания)
    async getVotingWithSignModel(actionKey: string): Promise<AxiosResponse<Record<string, IVotingWithSignModel>>> {
        return $api.get<Record<string, IVotingWithSignModel>>(
            `/documentExecution/getVotingWithSignModel/${this.documentId}/${actionKey}`,
        );
    }

    //Получить модель для голосования по вопросу
    async getVotingModel(actionKey: string): Promise<AxiosResponse<Record<string, IVotingModel>>> {
        return $api.get<Record<string, IVotingModel>>(
            `/documentExecution/getVotingModel/${this.documentId}/${actionKey}`,
        );
    }

    //Удаляет текущий документ из базы
    async deleteDocument(actionKey: string): Promise<AxiosResponse> {
        const ids = this.documentId?.split(',').filter((id) => id) ?? [];
        const url = `/documentExecution/deleteDocument/${actionKey}`;
        return withGroupRun(actionKey, (params) => (params ? $api.post(url, ids, { params }) : $api.post(url, ids)));
    }

    //Создает новый файл для дока
    async attachFiles(
        actionKey: string,
        attachProperties: any[],
        files: File[],
        onUploadProgress?: UploadProgressHandler,
    ): Promise<AxiosResponse> {
        let formData = new FormData();
        attachProperties.map((prop) => formData.append('attachProps', serializeAttachmentUpload(prop)));
        files.map((f) => formData.append('attachFiles', f));

        return $api.post(`documentExecution/attachFiles/${this.documentId}/${actionKey}`, formData, {
            onUploadProgress: createUploadProgressHandler(files, onUploadProgress),
        });
    }

    //Копирует документ
    async copyDocumentById(docId: string, actionKey: string): Promise<AxiosResponse<string>> {
        return $api.post(`documentExecution/copyDocument/${docId}/${actionKey}`);
    }

    //Копирует текущий документ
    async copyDocument(actionKey: string): Promise<AxiosResponse<string>> {
        return $api.post(`documentExecution/copyDocument/${this.documentId}/${actionKey}`);
    }

    // Запустить задачу автоимпорта/автоэкспорта
    async runAutoTask(runTaskData: IRunTaskModel): Promise<AxiosResponse<string>> {
        return $api.post(`/documentExecution/runAutoTask/${this.documentId}`, runTaskData);
    }

    //Создает новый файл для дока
    async loadFromFile(actionKey: string, file: File): Promise<AxiosResponse<Blob>> {
        let formData = new FormData();
        formData.append('actionKey', actionKey as string);
        formData.append('file', file as File);
        return $api.post<Blob>(`documentExecution/loadFromFile/${this.documentId}`, formData, {
            responseType: 'blob',
        });
    }

    /**
     * Загрузка из файла фоновой задачей: сервер кладёт файл в задачу и сразу отвечает 202. Отказы старта приходят
     * исходной AxiosError без тоста — их показывает окно ({@link BACKGROUND_START_REQUEST}).
     */
    async startLoadFromFileInBackground(actionKey: string, file: File): Promise<IBackgroundJobStarted> {
        const formData = new FormData();
        formData.append('actionKey', actionKey);
        formData.append('file', file);
        const path = this.documentId
            ? `documentExecution/loadFromFileInBackground/${encodeURIComponent(this.documentId)}`
            : 'documentExecution/loadFromFileInBackground';
        const response = await $api.post<IBackgroundJobStarted>(path, formData, BACKGROUND_START_REQUEST);
        return response.data;
    }

    // Возвращает модель для формы
    async getFormBuilderScheme(actionKey: string): Promise<AxiosResponse<IForms>> {
        return $api.get<IForms>(`/documentExecution/getFormBuilderScheme/${actionKey}`);
    }

    // Сохранить набор полей в документах
    async saveDocumentsBatchEditingData(
        documentIds: string,
        actionKey: string,
        data: IFormData,
        files?: ISelectedFiles,
        onUploadProgress?: UploadProgressHandler,
    ): Promise<AxiosResponse<IDocumentResult>> {
        const formData = new FormData();
        formData.append('documentIds', documentIds);
        formData.append('actionKey', actionKey);
        formData.append('data', JSON.stringify(data));

        if (files) {
            files.attachProperties.map((prop) => formData.append('attachProps', serializeAttachmentUpload(prop)));
            files.files.map((f) => formData.append('attachFiles', f));
        }

        const config = {
            suppressErrorNotification: true,
            onUploadProgress: createUploadProgressHandler(files?.files, onUploadProgress),
        };
        return withGroupRun(actionKey, (params) =>
            $api.post(
                `/documentExecution/saveDocumentsBatchEditingData`,
                formData,
                params ? { ...config, params } : config,
            ),
        );
    }

    //Получить модель для открытия документа (ИД документа)
    async getOpenDocumentModel(actionKey: string): Promise<AxiosResponse<IOpenDocumentModel>> {
        return $api.get<IOpenDocumentModel>(`/documentExecution/getOpenDocumentModel/${this.documentId}/${actionKey}`);
    }

    //Получить модель для открытия URL (ИД документа)
    async getOpenUrlModel(actionKey: string): Promise<AxiosResponse<IOpenUrlModel>> {
        return $api.get<IOpenUrlModel>(`/documentExecution/getOpenUrlModel/${this.documentId}/${actionKey}`);
    }

    // Возвращает модель данных для отправки на ЭТП
    async getSendToEtpModel(actionKey: string): Promise<AxiosResponse<ISendToEtpModel>> {
        return $api.get<ISendToEtpModel>(`/documentExecution/getSendToEtpModel/${this.documentId}/${actionKey}`);
    }

    // Отправка данных на ЭТП
    async sendToEtp(actionKey: string, sendToEtpData: ISendToEtpModel): Promise<AxiosResponse<string>> {
        return $api.post(`/documentExecution/sendToEtp/${this.documentId}/${actionKey}`, sendToEtpData);
    }

    // Возвращает модель данных для отправки в ЕИС
    async getSendToEisModel(actionKey: string): Promise<AxiosResponse<ISendToEisModel>> {
        return $api.get<ISendToEisModel>(`/documentExecution/getSendToEisModel/${this.documentId}/${actionKey}`);
    }

    // Отправка данных в ЕИС
    async sendToEis(actionKey: string, sendToEisData: ISendToEisModel): Promise<AxiosResponse<IActionResult>> {
        return $api.post(`/documentExecution/sendToEis/${this.documentId}/${actionKey}`, sendToEisData);
    }

    // Точный расчет размера пакета ЕИС без отправки
    async calculateSendToEisPackageSize(
        actionKey: string,
        sendToEisData: ISendToEisModel,
    ): Promise<AxiosResponse<ISendToEisPackageSize>> {
        return $api.post(
            `/documentExecution/calculateSendToEisPackageSize/${this.documentId}/${actionKey}`,
            sendToEisData,
        );
    }

    //Получить модель для копирования вложений
    async getCopyAttachmentsModel(actionKey: string): Promise<AxiosResponse<ICopyAttachmentsModel>> {
        return $api.get<ICopyAttachmentsModel>(
            `/documentExecution/getCopyAttachmentsModel/${this.documentId}/${actionKey}`,
        );
    }

    //Скопировать вложения в документ
    async copyAttachments(actionKey: string, selectedAttachments: ICopyAttachmentsResult): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/copyAttachments/${this.documentId}/${actionKey}`, selectedAttachments);
    }

    // Отправка электронной почты
    async sendCustomEmails(actionKey: string): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/sendCustomEmails/${this.documentId}/${actionKey}`);
    }

    //Удаляет лин документа
    /** `reason` — причина удаления связи, если у кнопки `requireReason`. */
    async removeLink(linkDocIdToRemove: string, actionKey: string, reason?: IActionReason): Promise<AxiosResponse> {
        return $api.delete(`documentExecution/removeLink/${this.documentId}/${linkDocIdToRemove}/${actionKey}`, {
            data: reason,
        });
    }

    //Выполнить переход
    async redirect(actionKey: string): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/redirect/${this.documentId}/${actionKey}`);
    }

    //Получить модель для создания документа из шаблона
    async getCreateDocByTemplateModel(actionKey: string): Promise<AxiosResponse<ICreateDocByTemplateModel>> {
        return $api.get<ICreateDocByTemplateModel>(
            `/documentExecution/getCreateDocByTemplateModel/${this.documentId}/${actionKey}`,
        );
    }

    //Создает документ из шаблона
    async createDocByTemplate(actionKey: string): Promise<AxiosResponse> {
        return $api.post(`documentExecution/createDocByTemplate/${this.documentId}/${actionKey}`);
    }

    //Получить модель для создания документа из шаблона (диалоговая версия)
    async getCreateDocByTemplateDialogModel(
        actionKey: string,
    ): Promise<AxiosResponse<ICreateDocByTemplateDialogModel>> {
        return $api.get<ICreateDocByTemplateDialogModel>(
            `/documentExecution/getCreateDocByTemplateDialogModel/${this.documentId}/${actionKey}`,
        );
    }

    //Создает документ из шаблона (диалоговая версия)
    async createDocByTemplateDialog(actionKey: string, templateIds: string[]): Promise<AxiosResponse> {
        let formData = new FormData();
        formData.append('templateIds', JSON.stringify(templateIds));
        return $api.post(`documentExecution/createDocByTemplateDialog/${this.documentId}/${actionKey}`, formData);
    }

    //Получить модель для создания напоминания
    async getMailReminderModel(actionKey: string): Promise<AxiosResponse<IMailReminderModel>> {
        return $api.get<IMailReminderModel>(`/documentExecution/getMailReminderModel/${this.documentId}/${actionKey}`);
    }

    //Создает напоминание по документу
    async createMailReminder(actionKey: string, deliveryDateTime: Date, message?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('message', message ?? '');
        formData.append('deliveryDateTimeUtc', deliveryDateTime.toUTCString());
        return $api.post(`documentExecution/createMailReminder/${this.documentId}/${actionKey}`, formData);
    }

    //Получить модель для связывания документов
    async getCreateDocumentLinkModel(actionKey: string): Promise<AxiosResponse<ICreateDocumentLinkModel>> {
        return $api.get<ICreateDocumentLinkModel>(
            `/documentExecution/getCreateDocumentLinkModel/${this.documentId}/${actionKey}`,
        );
    }

    //Получить данные для связывания документов
    async getCreateDocumentLinkData(
        actionKey: string,
        parameters: string[],
        formValues?: Record<string, any>,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ICreateDocumentLinkData>> {
        const queryParameters = [...parameters];
        if (formValues !== undefined) {
            queryParameters.push(`formValues=${encodeURIComponent(JSON.stringify(formValues))}`);
        }

        return $api.get<ICreateDocumentLinkData>(
            `/documentExecution/getCreateDocumentLinkData/${this.documentId}/${actionKey}${
                queryParameters.length > 0 ? `?${queryParameters.join('&')}` : ''
            }`,
            { signal },
        );
    }

    //Создает связи с документами
    async createDocumentLink(
        actionKey: string,
        docIds: string[],
        partsVersionToAttach?: string[],
    ): Promise<AxiosResponse> {
        let formData = new FormData();
        formData.append('docIds', JSON.stringify(docIds));
        formData.append('partsVersionToAttach', JSON.stringify(partsVersionToAttach));
        return $api.post(`documentExecution/createDocumentLink/${this.documentId}/${actionKey}`, formData);
    }

    async batchActivitiesComplete(actionKey: string, comment?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('comment', comment ?? '');

        return $api.post(`documentExecution/batchActivitiesComplete/${this.documentId}/${actionKey}`, formData);
    }

    //Получить модель для создания отчета
    async getCreateReportModel(actionKey: string): Promise<AxiosResponse<ICreateReportModel>> {
        return $api.get<ICreateReportModel>(`/documentExecution/getCreateReportModel/${this.documentId}/${actionKey}`);
    }

    /** Отчёт по документу фоновой задачей: тот же вход, что у POST `downloadReportDoc`; ответ 202 — `{ jobId }`. */
    async startReportInBackground(
        actionKey: string,
        request: { reportFormat?: string; formData?: IFormData },
    ): Promise<IBackgroundJobStarted> {
        const response = await $api.post<IBackgroundJobStarted>(
            `documentExecution/downloadReportDocInBackground/${encodeURIComponent(this.documentId)}/${encodeURIComponent(actionKey)}`,
            request,
            BACKGROUND_START_REQUEST,
        );
        return response.data;
    }

    /** Архив вложений документа фоновой задачей (кнопка DownloadAll); ответ 202 — `{ jobId }`. */
    startDownloadAllInBackground(actionKey: string): Promise<IBackgroundJobStarted> {
        return this.startArchiveInBackground('downloadAllInBackground', actionKey);
    }

    /** Архив вложений связанных документов фоновой задачей (кнопка DownloadLinkedDocumentsAttachments). */
    startLinkedDocumentsAttachmentsInBackground(actionKey: string): Promise<IBackgroundJobStarted> {
        return this.startArchiveInBackground('downloadLinkedDocumentsAttachmentsInBackground', actionKey);
    }

    private async startArchiveInBackground(route: string, actionKey: string): Promise<IBackgroundJobStarted> {
        const response = await $api.post<IBackgroundJobStarted>(
            `documentExecution/${route}/${encodeURIComponent(this.documentId)}/${encodeURIComponent(actionKey)}`,
            undefined,
            BACKGROUND_START_REQUEST,
        );
        return response.data;
    }

    //Создает шаблон документа на основе текущего документа
    async createDocumentTemplate(
        actionKey: string,
        templateName: string,
        templateAccess: string,
    ): Promise<AxiosResponse<string>> {
        let formData = new FormData();
        formData.append('templateName', templateName);
        formData.append('templateAccess', templateAccess);
        return $api.post(`documentExecution/createDocumentTemplate/${this.documentId}/${actionKey}`, formData);
    }

    //Создает шаблон документа на основе текущего документа
    async getEditDocumentTemplateModel(actionKey: string): Promise<AxiosResponse<IEditDocumentTemplateModel>> {
        return $api.get<IEditDocumentTemplateModel>(
            `documentExecution/getEditDocumentTemplateModel/${this.documentId}/${actionKey}`,
        );
    }

    //Создает шаблон документа на основе текущего документа
    async editDocumentTemplate(
        actionKey: string,
        templateName: string,
        templateAccess: string,
    ): Promise<AxiosResponse<string>> {
        let formData = new FormData();
        formData.append('templateName', templateName);
        formData.append('templateAccess', templateAccess);
        return $api.post(`documentExecution/editDocumentTemplate/${this.documentId}/${actionKey}`, formData);
    }
    //Получить модель для заполнения документа из шаблона
    async getFillDocFromTemplateModel(
        actionKey: string,
        documentData: IDocumentData,
    ): Promise<AxiosResponse<IFillDocFromTemplateModel>> {
        const formData = new FormData();
        formData.append('actionKey', actionKey);
        formData.append('documentData', JSON.stringify(documentData));

        return $api.post(`documentExecution/getFillDocFromTemplateModel/${actionKey}`, formData);
    }

    // Возвращает модель таблицы для BatchEditingTable
    async getBatchEditingTableScheme(actionKey: string, docId: string): Promise<AxiosResponse<IDocumentTable>> {
        return $api.get<IDocumentTable>(`/documentExecution/batchEditingTableScheme/${docId}/${actionKey}`);
    }

    // Возвращает данные таблицы для BatchEditingTable
    async getBatchEditingTableData(actionKey: string, docId: string): Promise<AxiosResponse<IBatchEditingTableData>> {
        return $api.get<IBatchEditingTableData>(`/documentExecution/batchEditingTableData/${docId}/${actionKey}`);
    }

    // Сохранить набор полей в документах
    async saveBatchEditingTableData(
        documentId: string,
        actionKey: string,
        data: IFormData,
    ): Promise<AxiosResponse<IDocumentResult>> {
        const formData = new FormData();
        formData.append('docId', documentId);
        formData.append('actionKey', actionKey);
        formData.append('data', JSON.stringify(data));

        return $api.post(`/documentExecution/saveBatchEditingTableData`, formData, {
            suppressErrorNotification: true,
        });
    }

    //Скопировать вложения в документ и изменить свойства
    async copyAndEditAttaches(actionKey: string, response: ICopyAndEditAttachesResponse): Promise<AxiosResponse> {
        return $api.post(`/documentExecution/copyAndEditAttaches/${this.documentId}/${actionKey}`, response);
    }
}
